import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import {
  FontAwesomeIcon,
} from "@fortawesome/react-fontawesome";

import {
  faArrowRotateRight,
  faBan,
  faBell,
  faBolt,
  faCalendarDays,
  faCirclePause,
  faCirclePlay,
  faClock,
  faEye,
  faFilter,
  faPaperPlane,
  faPen,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";

import toast from "react-hot-toast";

import {
  notificationCampaignsApi,
} from "../api/notificationCampaignsApi";

import type {
  CampaignSendMode,
  CampaignStatus,
  CampaignType,
  NotificationCampaign,
} from "../types/notification-campaigns.types";

import "../screenstyles/NotificationCampaigns.css";

const STATUS_OPTIONS:
  Array<{
    value:
      CampaignStatus |
      "";
    label: string;
  }> = [
    {
      value: "",
      label:
        "جميع الحالات",
    },

    {
      value: "draft",
      label: "مسودة",
    },

    {
      value:
        "scheduled",
      label: "مجدولة",
    },

    {
      value:
        "sending",
      label:
        "جاري الإرسال",
    },

    {
      value: "sent",
      label:
        "تم الإرسال",
    },

    {
      value:
        "paused",
      label:
        "متوقفة مؤقتًا",
    },

    {
      value:
        "failed",
      label: "فشلت",
    },

    {
      value:
        "cancelled",
      label: "ملغاة",
    },
  ];

const TYPE_OPTIONS:
  Array<{
    value:
      CampaignType |
      "";
    label: string;
  }> = [
    {
      value: "",
      label:
        "جميع الأنواع",
    },

    {
      value: "offers",
      label: "عروض",
    },

    {
      value:
        "discounts",
      label: "خصومات",
    },

    {
      value:
        "price_update",
      label:
        "تحديث أسعار",
    },

    {
      value: "points",
      label: "نقاط",
    },

    {
      value: "orders",
      label: "طلبات",
    },

    {
      value:
        "products",
      label: "منتجات",
    },

    {
      value: "general",
      label: "عام",
    },

    {
      value: "custom",
      label: "مخصص",
    },
  ];

const SEND_MODE_OPTIONS:
  Array<{
    value:
      CampaignSendMode |
      "";
    label: string;
  }> = [
    {
      value: "",
      label:
        "جميع طرق الإرسال",
    },

    {
      value: "now",
      label:
        "إرسال مباشر",
    },

    {
      value:
        "scheduled",
      label: "مجدول",
    },

    {
      value:
        "recurring",
      label: "متكرر",
    },
  ];

function getStatusLabel(
  status:
    CampaignStatus,
) {
  switch (status) {
    case "draft":
      return "مسودة";

    case "scheduled":
      return "مجدولة";

    case "sending":
      return "جاري الإرسال";

    case "sent":
      return "تم الإرسال";

    case "paused":
      return "متوقفة";

    case "failed":
      return "فشلت";

    case "cancelled":
      return "ملغاة";

    default:
      return status;
  }
}

function getTypeLabel(
  type: string,
) {
  switch (type) {
    case "offers":
      return "عروض";

    case "discounts":
      return "خصومات";

    case "price_update":
      return "تحديث أسعار";

    case "points":
      return "نقاط";

    case "orders":
      return "طلبات";

    case "products":
      return "منتجات";

    case "general":
      return "عام";

    case "custom":
      return "مخصص";

    default:
      return type;
  }
}

function getSendModeLabel(
  mode:
    CampaignSendMode,
) {
  switch (mode) {
    case "now":
      return "مباشر";

    case "scheduled":
      return "مجدول";

    case "recurring":
      return "متكرر";

    default:
      return mode;
  }
}

function formatTimestamp(
  value: any,
) {
  if (!value) {
    return "—";
  }

  let date:
    Date |
    null = null;

  if (
    typeof value ===
    "string"
  ) {
    date =
      new Date(value);
  } else {
    const seconds =
      value.seconds ??
      value._seconds;

    if (
      typeof seconds ===
      "number"
    ) {
      date =
        new Date(
          seconds *
            1000,
        );
    }
  }

  if (
    !date ||
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return "—";
  }

  return new Intl.DateTimeFormat(
    "ar-IQ",
    {
      year:
        "numeric",

      month:
        "short",

      day:
        "2-digit",

      hour:
        "2-digit",

      minute:
        "2-digit",
    },
  ).format(date);
}

const NotificationCampaigns:
React.FC = () => {
  const navigate =
    useNavigate();

  const [
    campaigns,
    setCampaigns,
  ] =
    useState<
      NotificationCampaign[]
    >([]);

  const [
    loading,
    setLoading,
  ] =
    useState(true);

  const [
    actionLoading,
    setActionLoading,
  ] =
    useState<
      string |
      null
    >(null);

  const [
    status,
    setStatus,
  ] =
    useState<
      CampaignStatus |
      ""
    >("");

  const [
    type,
    setType,
  ] =
    useState<
      CampaignType |
      ""
    >("");

  const [
    sendMode,
    setSendMode,
  ] =
    useState<
      CampaignSendMode |
      ""
    >("");

  const [
    enabled,
    setEnabled,
  ] =
    useState<
      "" |
      "true" |
      "false"
    >("");

  const [
    search,
    setSearch,
  ] =
    useState("");

  const [
    totalMatched,
    setTotalMatched,
  ] =
    useState(0);

  const loadCampaigns =
    useCallback(
      async () => {
        try {
          setLoading(
            true,
          );

          const result =
            await notificationCampaignsApi
              .list({
                status:
                  status ||
                  undefined,

                type:
                  type ||
                  undefined,

                sendMode:
                  sendMode ||
                  undefined,

                enabled:
                  enabled ===
                  ""
                    ? undefined
                    : enabled ===
                      "true",

                limit: 100,
              });

          setCampaigns(
            result.items ??
              [],
          );

          setTotalMatched(
            result.totalMatched ??
              result.count ??
              0,
          );
        } catch (
          error: any
        ) {
          console.error(
            "Failed to load notification campaigns:",
            error,
          );

          toast.error(
            error?.message ||
              "فشل تحميل حملات الإشعارات",
          );
        } finally {
          setLoading(
            false,
          );
        }
      },
      [
        status,
        type,
        sendMode,
        enabled,
      ],
    );

  useEffect(() => {
    loadCampaigns();
  }, [
    loadCampaigns,
  ]);

  const filteredCampaigns =
    useMemo(
      () => {
        const term =
          search
            .trim()
            .toLowerCase();

        if (!term) {
          return campaigns;
        }

        return campaigns.filter(
          campaign =>
            campaign.name
              .toLowerCase()
              .includes(
                term,
              ) ||
            campaign.title
              .toLowerCase()
              .includes(
                term,
              ) ||
            campaign.body
              .toLowerCase()
              .includes(
                term,
              ) ||
            campaign.campaignId
              .toLowerCase()
              .includes(
                term,
              ),
        );
      },
      [
        campaigns,
        search,
      ],
    );

  const stats =
    useMemo(
      () => ({
        total:
          campaigns.length,

        active:
          campaigns.filter(
            item =>
              item.enabled &&
              item.status !==
                "cancelled",
          ).length,

        scheduled:
          campaigns.filter(
            item =>
              item.status ===
              "scheduled",
          ).length,

        sent:
          campaigns.filter(
            item =>
              item.status ===
              "sent",
          ).length,
      }),
      [
        campaigns,
      ],
    );

  const runAction =
    async (
      campaign:
        NotificationCampaign,

      action:
        | "pause"
        | "resume"
        | "cancel"
        | "send-now",
    ) => {
      const actionKey =
        `${campaign.campaignId}:${action}`;

      try {
        setActionLoading(
          actionKey,
        );

        if (
          action ===
          "pause"
        ) {
          const confirmed =
            window.confirm(
              `هل تريد إيقاف حملة "${campaign.name}" مؤقتًا؟`,
            );

          if (!confirmed) {
            return;
          }

          await notificationCampaignsApi
            .pause(
              campaign.campaignId,
            );

          toast.success(
            "تم إيقاف الحملة مؤقتًا",
          );
        }

        if (
          action ===
          "resume"
        ) {
          const confirmed =
            window.confirm(
              `هل تريد استئناف حملة "${campaign.name}"؟`,
            );

          if (!confirmed) {
            return;
          }

          await notificationCampaignsApi
            .resume(
              campaign.campaignId,
            );

          toast.success(
            "تم استئناف الحملة",
          );
        }

        if (
          action ===
          "cancel"
        ) {
          const confirmed =
            window.confirm(
              `هل أنت متأكد من إلغاء حملة "${campaign.name}"؟`,
            );

          if (!confirmed) {
            return;
          }

          await notificationCampaignsApi
            .cancel(
              campaign.campaignId,
            );

          toast.success(
            "تم إلغاء الحملة",
          );
        }

        if (
          action ===
          "send-now"
        ) {
          const confirmed =
            window.confirm(
              `سيتم إرسال حملة "${campaign.name}" الآن. هل تريد المتابعة؟`,
            );

          if (!confirmed) {
            return;
          }

          const result =
            await notificationCampaignsApi
              .sendNow(
                campaign.campaignId,
              );

          toast.success(
            result.queued
              ? "تم وضع الحملة في قائمة الإرسال"
              : "تم تنفيذ طلب الإرسال",
          );
        }

        await loadCampaigns();
      } catch (
        error: any
      ) {
        console.error(
          "Campaign action failed:",
          error,
        );

        toast.error(
          error?.message ||
            "فشل تنفيذ العملية",
        );
      } finally {
        setActionLoading(
          null,
        );
      }
    };

  return (
    <div className="notification-campaigns-page">

      <section className="notification-campaigns-header">
        <div className="notification-campaigns-header-content">
          <span className="notification-campaigns-eyebrow">
            إدارة الإشعارات
          </span>

          <h1>
            حملات الإشعارات
          </h1>

          <p>
            إنشاء وإدارة ومراقبة
            الحملات المرسلة إلى
            مستخدمي تطبيق شوف.
          </p>
        </div>

        <div className="notification-campaigns-header-actions">
          <button
            type="button"
            className="notification-campaigns-refresh-btn"
            onClick={
              loadCampaigns
            }
            disabled={
              loading
            }
          >
            <FontAwesomeIcon
              icon={
                faArrowRotateRight
              }
            />

            تحديث
          </button>

          <button
            type="button"
            className="notification-campaigns-add-btn"
            onClick={() =>
              navigate(
                "/notification-campaigns/add",
              )
            }
          >
            <FontAwesomeIcon
              icon={faPlus}
            />

            حملة جديدة
          </button>
        </div>
      </section>

      <section className="notification-campaigns-stats">
        <div className="notification-campaign-stat-card">
          <span>
            إجمالي الحملات
          </span>

          <strong>
            {totalMatched ||
              stats.total}
          </strong>

          <FontAwesomeIcon
            icon={faBell}
          />
        </div>

        <div className="notification-campaign-stat-card">
          <span>
            الحملات الفعالة
          </span>

          <strong>
            {stats.active}
          </strong>

          <FontAwesomeIcon
            icon={faBolt}
          />
        </div>

        <div className="notification-campaign-stat-card">
          <span>
            الحملات المجدولة
          </span>

          <strong>
            {stats.scheduled}
          </strong>

          <FontAwesomeIcon
            icon={
              faCalendarDays
            }
          />
        </div>

        <div className="notification-campaign-stat-card">
          <span>
            تم إرسالها
          </span>

          <strong>
            {stats.sent}
          </strong>

          <FontAwesomeIcon
            icon={
              faPaperPlane
            }
          />
        </div>
      </section>

      <section className="notification-campaigns-toolbar">
        <div className="notification-campaigns-search">
          <FontAwesomeIcon
            icon={faFilter}
          />

          <input
            type="text"
            placeholder="بحث بالاسم أو عنوان الإشعار..."
            value={search}
            onChange={
              event =>
                setSearch(
                  event.target
                    .value,
                )
            }
          />
        </div>

        <select
          value={status}
          onChange={
            event =>
              setStatus(
                event.target
                  .value as
                  CampaignStatus |
                  "",
              )
          }
        >
          {STATUS_OPTIONS.map(
            option => (
              <option
                key={
                  option.value ||
                  "all"
                }
                value={
                  option.value
                }
              >
                {
                  option.label
                }
              </option>
            ),
          )}
        </select>

        <select
          value={type}
          onChange={
            event =>
              setType(
                event.target
                  .value as
                  CampaignType |
                  "",
              )
          }
        >
          {TYPE_OPTIONS.map(
            option => (
              <option
                key={
                  option.value ||
                  "all"
                }
                value={
                  option.value
                }
              >
                {
                  option.label
                }
              </option>
            ),
          )}
        </select>

        <select
          value={
            sendMode
          }
          onChange={
            event =>
              setSendMode(
                event.target
                  .value as
                  CampaignSendMode |
                  "",
              )
          }
        >
          {SEND_MODE_OPTIONS.map(
            option => (
              <option
                key={
                  option.value ||
                  "all"
                }
                value={
                  option.value
                }
              >
                {
                  option.label
                }
              </option>
            ),
          )}
        </select>

        <select
          value={enabled}
          onChange={
            event =>
              setEnabled(
                event.target
                  .value as
                  | ""
                  | "true"
                  | "false",
              )
          }
        >
          <option value="">
            الكل
          </option>

          <option value="true">
            مفعلة
          </option>

          <option value="false">
            غير مفعلة
          </option>
        </select>
      </section>

      <section className="notification-campaigns-table-card">
        {loading ? (
          <div className="notification-campaigns-state">
            جاري تحميل حملات الإشعارات...
          </div>
        ) : filteredCampaigns.length ===
          0 ? (
          <div className="notification-campaigns-state">
            <FontAwesomeIcon
              icon={faBell}
            />

            <h3>
              لا توجد حملات
            </h3>

            <p>
              لا توجد حملات إشعارات
              تطابق خيارات البحث الحالية.
            </p>
          </div>
        ) : (
          <div className="notification-campaigns-table-wrapper">
            <table className="notification-campaigns-table">
              <thead>
                <tr>
                  <th>
                    الحملة
                  </th>

                  <th>
                    النوع
                  </th>

                  <th>
                    الحالة
                  </th>

                  <th>
                    الإرسال
                  </th>

                  <th>
                    الاستهداف
                  </th>

                  <th>
                    الموعد
                  </th>

                  <th>
                    الإجراءات
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredCampaigns.map(
                  campaign => {
                    const audienceCount =
                      campaign.target
                        ?.userIds
                        ?.length ??
                      0;

                    return (
                      <tr
                        key={
                          campaign.campaignId
                        }
                      >
                        <td>
                          <div className="notification-campaign-name-cell">
                            {campaign.imageUrl ? (
                              <img
                                src={
                                  campaign.imageUrl
                                }
                                alt=""
                              />
                            ) : (
                              <span className="notification-campaign-icon">
                                <FontAwesomeIcon
                                  icon={
                                    faBell
                                  }
                                />
                              </span>
                            )}

                            <div>
                              <strong>
                                {
                                  campaign.name
                                }
                              </strong>

                              <span>
                                {
                                  campaign.title
                                }
                              </span>

                              <small>
                                {
                                  campaign.body
                                }
                              </small>
                            </div>
                          </div>
                        </td>

                        <td>
                          <span className="notification-campaign-type">
                            {getTypeLabel(
                              campaign.type,
                            )}
                          </span>
                        </td>

                        <td>
                          <span
                            className={`notification-campaign-status status-${campaign.status}`}
                          >
                            {getStatusLabel(
                              campaign.status,
                            )}
                          </span>
                        </td>

                        <td>
                          <div className="notification-campaign-send-mode">
                            <FontAwesomeIcon
                              icon={
                                campaign.sendMode ===
                                "now"
                                  ? faBolt
                                  : faClock
                              }
                            />

                            {getSendModeLabel(
                              campaign.sendMode,
                            )}
                          </div>
                        </td>

                        <td>
                          {audienceCount >
                          0
                            ? `${audienceCount} مستخدم محدد`
                            : "حسب الفلاتر"}
                        </td>

                        <td>
                          {campaign.sendMode ===
                          "scheduled"
                            ? formatTimestamp(
                                campaign.scheduledAt,
                              )
                            : campaign.sendMode ===
                                "recurring"
                              ? "جدولة متكررة"
                              : "مباشر"}
                        </td>

                        <td>
                          <div className="notification-campaign-actions">
                            <button
                              type="button"
                              className="campaign-action view"
                              title="عرض التفاصيل"
                              onClick={() =>
                                navigate(
                                  `/notification-campaigns/${campaign.campaignId}`,
                                )
                              }
                            >
                              <FontAwesomeIcon
                                icon={
                                  faEye
                                }
                              />
                            </button>

                            {campaign.status !==
                              "sent" &&
                              campaign.status !==
                                "cancelled" && (
                                <button
                                  type="button"
                                  className="campaign-action edit"
                                  title="تعديل"
                                  onClick={() =>
                                    navigate(
                                      `/notification-campaigns/${campaign.campaignId}/edit`,
                                    )
                                  }
                                >
                                  <FontAwesomeIcon
                                    icon={
                                      faPen
                                    }
                                  />
                                </button>
                              )}

                            {campaign.status ===
                              "paused" ? (
                              <button
                                type="button"
                                className="campaign-action resume"
                                title="استئناف"
                                disabled={
                                  actionLoading !==
                                  null
                                }
                                onClick={() =>
                                  runAction(
                                    campaign,
                                    "resume",
                                  )
                                }
                              >
                                <FontAwesomeIcon
                                  icon={
                                    faCirclePlay
                                  }
                                />
                              </button>
                            ) : (
                              campaign.status !==
                                "sent" &&
                              campaign.status !==
                                "cancelled" &&
                              campaign.status !==
                                "failed" && (
                                <button
                                  type="button"
                                  className="campaign-action pause"
                                  title="إيقاف مؤقت"
                                  disabled={
                                    actionLoading !==
                                    null
                                  }
                                  onClick={() =>
                                    runAction(
                                      campaign,
                                      "pause",
                                    )
                                  }
                                >
                                  <FontAwesomeIcon
                                    icon={
                                      faCirclePause
                                    }
                                  />
                                </button>
                              )
                            )}

                            {campaign.status !==
                              "sending" &&
                              campaign.status !==
                                "sent" &&
                              campaign.status !==
                                "cancelled" && (
                                <button
                                  type="button"
                                  className="campaign-action send"
                                  title="إرسال الآن"
                                  disabled={
                                    actionLoading !==
                                    null
                                  }
                                  onClick={() =>
                                    runAction(
                                      campaign,
                                      "send-now",
                                    )
                                  }
                                >
                                  <FontAwesomeIcon
                                    icon={
                                      faPaperPlane
                                    }
                                  />
                                </button>
                              )}

                            {campaign.status !==
                              "sent" &&
                              campaign.status !==
                                "cancelled" && (
                                <button
                                  type="button"
                                  className="campaign-action cancel"
                                  title="إلغاء الحملة"
                                  disabled={
                                    actionLoading !==
                                    null
                                  }
                                  onClick={() =>
                                    runAction(
                                      campaign,
                                      "cancel",
                                    )
                                  }
                                >
                                  <FontAwesomeIcon
                                    icon={
                                      faBan
                                    }
                                  />
                                </button>
                              )}
                          </div>
                        </td>
                      </tr>
                    );
                  },
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

    </div>
  );
};

export default NotificationCampaigns;